import type { LinkQuality } from '@freedom/shared';
import { earthCurvatureBulgeM, fresnelRadiusM } from './index.js';

export interface LosInputSample {
  distanceM: number;
  terrainElevationM: number;
  effectiveSurfaceM: number;
  source: string;
  obstacleName?: string;
}

export interface LosProfileSample extends LosInputSample {
  earthCurvatureM: number;
  correctedSurfaceM: number;
  lineOfSightElevationM: number;
  fresnelRadiusM: number;
  fresnel60LowerM: number;
  fresnel100LowerM: number;
  losClearanceM: number;
  fresnelClearancePercent: number;
}

export interface CriticalPoint {
  distanceM: number;
  clearanceM: number;
  fresnelClearancePercent: number;
  surfaceElevationM: number;
  lineOfSightElevationM: number;
  obstacleName: string | null;
  source: string;
}

export interface LosDiagnostics {
  visible: boolean;
  quality: LinkQuality;
  minimumFresnelClearancePercent: number;
  minimumLosClearanceM: number;
  requiredClearancePercent: number;
  criticalPoint: CriticalPoint;
  blockedSampleCount: number;
  marginalSampleCount: number;
  kFactor: number;
  earthRadiusM: number;
  profile: LosProfileSample[];
}

export interface LosAnalysisInput {
  frequencyGHz: number;
  startAntennaElevationM: number;
  endAntennaElevationM: number;
  samples: LosInputSample[];
  kFactor?: number;
  requiredClearancePercent?: number;
}

const EARTH_RADIUS_M = 6_371_000;

export function analyzeLineOfSight(input: LosAnalysisInput): LosDiagnostics {
  if (input.samples.length < 2) throw new Error('LOS analysis requires at least two profile samples');
  if (input.frequencyGHz <= 0) throw new RangeError('Frequency must be positive');
  const kFactor = input.kFactor ?? 4 / 3;
  const requiredClearancePercent = input.requiredClearancePercent ?? 60;
  if (kFactor <= 0) throw new RangeError('K-factor must be positive');
  const totalDistanceM = input.samples[input.samples.length - 1]?.distanceM ?? 0;
  if (totalDistanceM <= 0) throw new RangeError('Profile distance must be positive');

  const profile = input.samples.map((sample): LosProfileSample => {
    const fraction = sample.distanceM / totalDistanceM;
    const lineOfSightElevationM = input.startAntennaElevationM + (input.endAntennaElevationM - input.startAntennaElevationM) * fraction;
    const distanceToEndM = totalDistanceM - sample.distanceM;
    const earthCurvatureM = earthCurvatureBulgeM(sample.distanceM, distanceToEndM, kFactor);
    const correctedSurfaceM = sample.effectiveSurfaceM + earthCurvatureM;
    const fresnel = fresnelRadiusM(input.frequencyGHz, sample.distanceM, distanceToEndM);
    const losClearanceM = lineOfSightElevationM - correctedSurfaceM;
    const fresnelClearancePercent = fresnel < 0.001 ? Number.POSITIVE_INFINITY : (losClearanceM / fresnel) * 100;
    return {
      ...sample,
      earthCurvatureM,
      correctedSurfaceM,
      lineOfSightElevationM,
      fresnelRadiusM: fresnel,
      fresnel60LowerM: lineOfSightElevationM - fresnel * 0.6,
      fresnel100LowerM: lineOfSightElevationM - fresnel,
      losClearanceM,
      fresnelClearancePercent,
    };
  });

  const interior = profile.slice(1, -1);
  const candidates = interior.length > 0 ? interior : profile;
  const critical = candidates.reduce((minimum, sample) => sample.fresnelClearancePercent < minimum.fresnelClearancePercent ? sample : minimum);
  const minimumLosClearanceM = Math.min(...candidates.map((sample) => sample.losClearanceM));
  const minimumFresnelClearancePercent = critical.fresnelClearancePercent;
  const visible = minimumLosClearanceM >= 0;
  const quality: LinkQuality = !visible ? 'blocked' : minimumFresnelClearancePercent >= 100 ? 'excellent' : minimumFresnelClearancePercent >= requiredClearancePercent ? 'good' : 'marginal';

  return {
    visible,
    quality,
    minimumFresnelClearancePercent,
    minimumLosClearanceM,
    requiredClearancePercent,
    criticalPoint: {
      distanceM: critical.distanceM,
      clearanceM: critical.losClearanceM,
      fresnelClearancePercent: critical.fresnelClearancePercent,
      surfaceElevationM: critical.correctedSurfaceM,
      lineOfSightElevationM: critical.lineOfSightElevationM,
      obstacleName: critical.obstacleName ?? null,
      source: critical.source,
    },
    blockedSampleCount: candidates.filter((sample) => sample.losClearanceM < 0).length,
    marginalSampleCount: candidates.filter((sample) => sample.losClearanceM >= 0 && sample.fresnelClearancePercent < requiredClearancePercent).length,
    kFactor,
    earthRadiusM: EARTH_RADIUS_M,
    profile,
  };
}
